export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last Updated: October 28, 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700">
              Hello.Reviews ("we," "our," "us") is committed to protecting your privacy. This Privacy Policy explains
              how we collect, use, share, and protect information when you use our review management services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">1. Business Information (From You)</h3>
              <p className="text-gray-700 mb-2">When you sign up, we collect:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Business name and contact information</li>
                <li>Email address and phone number</li>
                <li>Business address and industry</li>
                <li>Payment information (processed securely)</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2. Customer Information (From You)</h3>
              <p className="text-gray-700 mb-2">When you use our service, you provide:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Customer names and contact information</li>
                <li>Service dates and notes</li>
                <li>Any other customer data you choose to share</li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-3">
                <p className="text-gray-800 font-semibold">Important:</p>
                <p className="text-gray-700">You are responsible for having the legal right to share customer information with us.</p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">3. Review Data</h3>
              <p className="text-gray-700 mb-2">We collect:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Reviews collected from your customers</li>
                <li>Review platform data (Google, Yelp, etc.)</li>
                <li>Review analytics and metrics</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>To Provide Services:</strong> Send review requests, collect reviews, generate reports</li>
              <li><strong>To Improve:</strong> Analyze usage patterns, fix bugs, develop features</li>
              <li><strong>To Communicate:</strong> Service updates, support, billing notifications</li>
              <li><strong>To Comply:</strong> Meet legal obligations, enforce Terms of Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Share Your Information</h2>

            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">We DO Share With:</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Service Providers:</strong> Email delivery, SMS, payment processing, cloud hosting</li>
                <li><strong>With Your Consent:</strong> Review platforms when posting on your behalf</li>
                <li><strong>Legal Requirements:</strong> When required by law or court order</li>
              </ul>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">We DO NOT Share With:</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>❌ Advertisers</li>
                <li>❌ Data brokers</li>
                <li>❌ Your competitors</li>
                <li>❌ Third parties for their marketing</li>
                <li>❌ Anyone else without your consent</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Customer Data</h2>
            <p className="text-gray-700 mb-4"><strong>How We Handle Customer Information:</strong></p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>We treat your customer data as confidential</li>
              <li>We use it ONLY to deliver services to you</li>
              <li>We do not sell or rent customer data</li>
              <li>We delete it when you cancel (within 30 days)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 mb-2">We protect your data with:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Encryption:</strong> SSL/TLS for data in transit</li>
              <li><strong>Access Controls:</strong> Limited team access on need-to-know basis</li>
              <li><strong>Secure Storage:</strong> Cloudflare infrastructure with encryption</li>
              <li><strong>Regular Backups:</strong> Automated daily backups</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Privacy Rights</h2>
            <p className="text-gray-700 mb-2">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Access:</strong> See what data we have</li>
              <li><strong>Correction:</strong> Fix inaccurate data</li>
              <li><strong>Deletion:</strong> Request data deletion</li>
              <li><strong>Portability:</strong> Get your data in transferable format</li>
              <li><strong>Opt Out:</strong> Stop marketing emails</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700">Privacy questions or requests?</p>
            <p className="text-gray-700 mt-2">
              <strong>Website:</strong> <a href="https://hello.reviews" className="text-blue-600 hover:underline">https://hello.reviews</a>
            </p>
            <p className="text-gray-700">
              <strong>Response Time:</strong> Within 5 business days
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Compliance</h2>
            <p className="text-gray-700 mb-2">We comply with:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>General Data Protection Regulation (GDPR)</li>
              <li>California Consumer Privacy Act (CCPA)</li>
              <li>CAN-SPAM Act</li>
              <li>Review platform policies (Google, Yelp, etc.)</li>
            </ul>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-8">
            <p className="text-sm text-gray-600 italic">
              This policy is subject to change. Last updated: October 28, 2025
            </p>
            <p className="text-sm text-gray-600 mt-2 italic">
              This is a legal document. We recommend having an attorney review it for your specific situation.
            </p>
            <p className="text-sm text-gray-600 mt-4">
              <a href="/#home" className="text-blue-600 hover:underline">← Back to Home</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
